<template>
  <div>
    <div id="page-panel">
      <div class="customer-top-nav">
        <div class="title">
          <span>Prevision des demandes d'importation</span><br>
        </div>
        <div class="controls no-print">
          <el-button type="primary" size="small" @click="fetchData" :loading="loading">
            Actualiser
          </el-button>
        </div>
      </div>

      <p>
        <span class="fs-14 secondary-text">{{ companyName }}</span>
      </p>
      <p class="legend-line secondary-text">
        Ligne du haut: inventaire estime en bouteilles | Ligne du bas: reception / a recevoir
      </p>

      <el-table
        v-loading="loading"
        :data="rows"
        border
        stripe
        empty-text="Aucun produit en bon de commande pour cette annee"
        style="width: 100%;"
        height="650">
        <el-table-column prop="sku" label="SKU" width="100" fixed="left" sortable />
        <el-table-column prop="name" label="Produit" min-width="280" fixed="left" sortable>
          <template slot-scope="scope">
            <div class="cell-product-name">{{ scope.row.name }}</div>
            <div class="cell-product-meta">{{ scope.row.provider_name }}<span v-if="scope.row.uvc"> &middot; UVC&nbsp;{{ scope.row.uvc }}</span><span v-if="scope.row.lblFormat"> &middot; {{ scope.row.lblFormat }}</span></div>
          </template>
        </el-table-column>

        <el-table-column label="Semaines" align="center">
          <el-table-column
            v-for="month in weekGroups"
            :key="month.groupKey"
            :label="month.label"
            align="center">
            <el-table-column
              v-for="week in month.weeks"
              :key="week.key"
              :prop="`weeklyQuantities.${week.key}`"
              :label="week.startLabel"
              width="60"
              align="right"
              :class-name="week.isPast ? 'past-week-col' : ''"
              :label-class-name="week.isPast ? 'past-week-col' : ''">
              <template slot-scope="scope">
                <span :class="{ 'past-week-cell': week.isPast }">
                  <div class="remaining-line" :class="{ 'remaining-line-negative': (scope.row.weeklyRemainingNum[week.key] || 0) < 0 }">
                    {{ scope.row.weeklyRemainingDisplay[week.key] || '~0' }}
                  </div>
                  <div class="flow-line">
                    {{ scope.row.weeklyFlowDisplay[week.key] || '0' }}
                  </div>
                </span>
              </template>
            </el-table-column>
          </el-table-column>
        </el-table-column>

        <el-table-column prop="totalQty" label="Total" width="110" align="right" fixed="right" sortable :sort-method="sortByTotalQty">
          <template slot-scope="scope">
            <strong>{{ formatSignedQty(scope.row.totalQty) }}</strong>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapActions, mapState } from 'vuex';
import InventoryAPI from '@/services/API/inventory';

export default {
  name: 'PurchaseOrdersForecastReport',
  props: {
    width: {
      type: Number,
      default: 100,
    },
    companyName: '',
  },
  data() {
    return {
      loading: false,
      weekGroups: [],
      rows: [],
    };
  },
  computed: {
    myInventory() {
      return _.find(this.inventories, inv => inv.code === 'IP');
    },
    ...mapState('inventory', ['inventories']),
  },
  methods: {
    ...mapActions('inventory', ['fetchInventories']),
    formatQty(value) {
      return Math.round(Number(value || 0));
    },
    formatSignedQty(value) {
      const qty = this.formatQty(value);
      return qty > 0 ? `+${qty}` : `${qty}`;
    },
    formatInventoryQty(value) {
      const qty = this.formatQty(value);
      return `~${qty < 0 ? 0 : qty}`;
    },
    sortByTotalQty(a, b) {
      return this.formatQty(a.totalQty) - this.formatQty(b.totalQty);
    },
    formatDateDDMM(date) {
      const dd = String(date.getDate()).padStart(2, '0');
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      return `${dd}/${mm}`;
    },
    getIsoWeekStartDate(year, weekNumber) {
      // ISO week 1 is the week containing January 4th, starting on Monday.
      const jan4 = new Date(year, 0, 4);
      const jan4IsoDay = (jan4.getDay() + 6) % 7;
      const week1Monday = new Date(year, 0, 4 - jan4IsoDay);
      const start = new Date(week1Monday);
      start.setDate(week1Monday.getDate() + ((Number(weekNumber) - 1) * 7));
      return start;
    },
    buildWeekGroups(rawWeeks) {
      const MONTHS_FR = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const weeks = _.map(rawWeeks || [], (week) => {
        const start = this.getIsoWeekStartDate(week.isoYear, week.weekNumber);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        return {
          ...week,
          startLabel: this.formatDateDDMM(start),
          monthIndex: start.getMonth(),
          calYear: start.getFullYear(),
          isPast: end < today,
        };
      });

      const groups = [];
      _.forEach(weeks, (week) => {
        const groupKey = `${week.calYear}-${week.monthIndex}`;
        const existing = _.find(groups, g => g.groupKey === groupKey);
        if (existing) {
          existing.weeks.push(week);
        } else {
          groups.push({
            groupKey,
            monthIndex: week.monthIndex,
            label: MONTHS_FR[week.monthIndex],
            weeks: [week],
          });
        }
      });
      return groups;
    },
    fetchData() {
      if (!_.get(this.myInventory, 'id')) {
        this.weekGroups = [];
        this.rows = [];
        return Promise.resolve();
      }

      this.loading = true;
      const now = new Date();
      const pad2 = n => String(n).padStart(2, '0');
      const toDate = d => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
      const startDate = toDate(new Date(now.getFullYear(), now.getMonth() - 1, 1));
      const endDate = toDate(new Date(now.getFullYear(), now.getMonth() + 19, 0));
      return InventoryAPI.fetchPurchaseOrdersForecast(this.myInventory.id, { startDate, endDate })
        .then((data) => {
          const weekGroups = this.buildWeekGroups(_.get(data, 'weeks', []));
          const flatWeeks = _.flatMap(weekGroups, g => g.weeks);
          this.weekGroups = weekGroups;

          this.rows = _.chain(_.get(data, 'rows', []))
            .map(row => ({
              ...row,
              totalQty: this.formatQty(row.totalQty),
              weeklyFlowDisplay: _.reduce(flatWeeks, (acc, week) => {
                acc[week.key] = this.formatSignedQty(_.get(row, `weeklyQuantities.${week.key}`, 0));
                return acc;
              }, {}),
              weeklyRemainingNum: _.reduce(flatWeeks, (acc, week) => {
                acc[week.key] = this.formatQty(_.get(row, `weeklyRemaining.${week.key}`, 0));
                return acc;
              }, {}),
              weeklyRemainingDisplay: _.reduce(flatWeeks, (acc, week) => {
                acc[week.key] = this.formatInventoryQty(_.get(row, `weeklyRemaining.${week.key}`, 0));
                return acc;
              }, {}),
            }))
            .orderBy(['provider_name', 'name'], ['asc', 'asc'])
            .value();
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          this.weekGroups = [];
          this.rows = [];
          this.$message.error('Une erreur systeme est survenue. Veuillez reessayer plus tard');
        });
    },
  },
  created() {
    this.fetchInventories().then(() => this.fetchData());
  },
};
</script>

<style lang="scss" scoped>
@import '../../../../../assets/scss/_variables';

.title {
  font-size: 1.6em;
  font-weight: bold;
  margin-right: 20px;
  position: relative;
  float: left;
}

.customer-top-nav {
  height: 40px;
}

.controls {
  position: relative;
  float: right;

  button {
    margin-left: 10px;
  }
}

::v-deep .el-table td,
::v-deep .el-table th {
  padding: 3px 0;
}

::v-deep .el-table .cell {
  font-size: 11px;
  padding: 0 6px;
}

.cell-product-name {
  font-weight: 500;
  line-height: 1.3;
}

.cell-product-meta {
  font-size: 10px;
  color: #909399;
  line-height: 1.3;
}

.legend-line {
  font-size: 11px;
  margin-top: -8px;
  margin-bottom: 6px;
}

::v-deep .past-week-col {
  background-color: #f5f5f5 !important;
}

.past-week-cell {
  color: #c0c4cc;
}

.remaining-line {
  font-size: 10px;
  font-weight: 600;
  line-height: 1.1;
  color: #606266;
}

.remaining-line-negative {
  color: #f56c6c;
}

.flow-line {
  font-weight: 700;
}
</style>
